import { ColumnEditorOptions } from "primereact/column";
import { Question } from "../../types/checklist";
import {
    BooleanEditor,
    booleanDisplayTemplate,
    SingleChoiceEditor,
    singleChoiceDisplayTemplate,
    MultiChoiceEditor,
    multiChoiceDisplayTemplate,
    NumberEditor,
    numberDisplayTemplate,
    DateEditor,
    dateDisplayTemplate,
    TextEditor,
} from "./editors";

interface AnswerEditorProps {
    options: ColumnEditorOptions;
    question?: Question;
}

/**
 * Dynamic answer editor that renders the appropriate input control
 * based on the question type from the checklist configuration.
 */
export const AnswerEditor = ({ options, question }: AnswerEditorProps) => {
    // If no question config available, fall back to text editor
    if (!question) {
        return <TextEditor options={options} />;
    }

    switch (question.type) {
        case "boolean":
            return <BooleanEditor options={options} />;

        case "single-choice":
            if (!question.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <SingleChoiceEditor
                    options={options}
                    questionOptions={question.options}
                />
            );

        case "multi-choice":
            if (!question.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <MultiChoiceEditor
                    options={options}
                    questionOptions={question.options}
                />
            );

        case "number":
            return (
                <NumberEditor
                    options={options}
                    unit={question.unit}
                    min={question.validation?.min}
                    max={question.validation?.max}
                />
            );

        case "date":
            return <DateEditor options={options} />;

        case "text":
            return (
                <TextEditor
                    options={options}
                    maxLength={question.validation?.maxLength}
                />
            );

        case "table":
            // Table type is complex, fall back to text for now
            return <TextEditor options={options} />;

        default:
            return <TextEditor options={options} />;
    }
};

/**
 * Get the display value for an answer based on question type.
 * Converts internal values (option IDs, booleans) to human-readable text.
 */
export const getAnswerDisplayValue = (value: unknown, question?: Question): string => {
    if (value === null || value === undefined) return "";

    if (!question) {
        return String(value);
    }

    switch (question.type) {
        case "boolean":
            return booleanDisplayTemplate(value);

        case "single-choice":
            return singleChoiceDisplayTemplate(value, question.options);

        case "multi-choice":
            return multiChoiceDisplayTemplate(value, question.options);

        case "number":
            return numberDisplayTemplate(value, question.unit);

        case "date":
            return dateDisplayTemplate(value);

        default:
            return String(value);
    }
};
